import { FormEvent, useEffect, useMemo, useState } from "react";
import PageBreadcrumbs from "../../layouts/PageBreadcrumbs.js";
import { SkeletonTable } from "../../components/Skeleton.js";
import { api, ApiError, PlanCapabilities, PlanCatalogEntry, PlanLimits, SubscriptionPackage } from "../../lib/api.js";
import EmptyState from "../../components/EmptyState.js";
import Drawer from "../../components/Drawer.js";
import { useTableState } from "../../lib/useTableState.js";
import TableToolbar from "../../components/TableToolbar.js";

const TIERS: PlanCatalogEntry["tier"][] = ["starter", "professional", "enterprise", "government"];

const LIMIT_FIELDS: Array<{ key: keyof PlanLimits; label: string; help?: string }> = [
  { key: "seats", label: "Seats", help: "Max workspace members" },
  { key: "datasets", label: "Datasets" },
  { key: "dhis2Instances", label: "DHIS2 instances" },
  { key: "publicPortals", label: "Public portals" },
  { key: "scheduledJobs", label: "Scheduled jobs" },
  { key: "reports", label: "Reports" },
  { key: "scheduledReports", label: "Scheduled reports" },
  { key: "externalIntegrations", label: "External integrations" },
  { key: "apiCallsPerMonth", label: "API calls / month" },
  { key: "dataRetentionDays", label: "Data retention (days)" },
  { key: "warehouseQueryRowsPerRun", label: "Warehouse query rows / run" },
  { key: "storageGb", label: "Storage (GB)" }
];

const CAPABILITY_FIELDS: Array<{ key: keyof PlanCapabilities; label: string; help?: string }> = [
  { key: "warehouseExplorer", label: "Warehouse explorer" },
  { key: "monitoringUi", label: "Monitoring UI" },
  { key: "scheduling", label: "Scheduling" },
  { key: "visualisationPlatform", label: "Visualisation platform" },
  { key: "customReports", label: "Custom reports" },
  { key: "scheduledReports", label: "Scheduled reports" },
  { key: "externalIntegrations", label: "External integrations" },
  { key: "publicPortal", label: "Public portal" },
  { key: "privatePortal", label: "Private portal" },
  { key: "sso", label: "SSO" },
  { key: "auditLogExport", label: "Audit log export" },
  { key: "priorityJobQueue", label: "Priority job queue" },
  { key: "multiRegion", label: "Multi-region" },
  { key: "premiumSupport", label: "Premium support" }
];

const EMPTY_LIMITS: PlanLimits = {
  seats: 0, datasets: 0, dhis2Instances: 0, publicPortals: 0,
  scheduledJobs: 0, reports: 0, scheduledReports: 0, externalIntegrations: 0,
  apiCallsPerMonth: 0, dataRetentionDays: 0, warehouseQueryRowsPerRun: 0, storageGb: 0
};

const EMPTY_CAPS: PlanCapabilities = {
  warehouseExplorer: false, monitoringUi: false, scheduling: false, visualisationPlatform: false,
  customReports: false, scheduledReports: false, externalIntegrations: false, publicPortal: false,
  privatePortal: false, sso: false, auditLogExport: false, priorityJobQueue: false,
  multiRegion: false, premiumSupport: false
};

type Draft = Omit<PlanCatalogEntry, "priceMonthlyUsd" | "limits"> & {
  priceMonthlyUsd: string;
  limits: Record<keyof PlanLimits, string>;
  featuresText: string;
};

function planToDraft(plan: PlanCatalogEntry | null): Draft {
  const base: PlanCatalogEntry = plan ?? {
    id: "",
    name: "",
    tier: "starter",
    priceMonthlyUsd: 0,
    seatLimit: 0,
    datasetLimit: 0,
    limits: EMPTY_LIMITS,
    capabilities: EMPTY_CAPS,
    features: [],
    description: "",
    recommended: false,
    waiveFees: false,
    waiveFeesReason: ""
  };
  const limitStrs = {} as Record<keyof PlanLimits, string>;
  for (const f of LIMIT_FIELDS) limitStrs[f.key] = String(base.limits[f.key] ?? 0);
  return {
    id: base.id,
    name: base.name,
    tier: base.tier,
    priceMonthlyUsd: String(base.priceMonthlyUsd),
    seatLimit: base.seatLimit,
    datasetLimit: base.datasetLimit,
    limits: limitStrs,
    capabilities: { ...EMPTY_CAPS, ...base.capabilities },
    features: base.features,
    featuresText: base.features.join("\n"),
    description: base.description ?? "",
    recommended: base.recommended ?? false,
    waiveFees: base.waiveFees ?? false,
    waiveFeesReason: base.waiveFeesReason ?? ""
  };
}

function packageToPlanDraft(pkg: SubscriptionPackage): Draft {
  const limits = { ...EMPTY_LIMITS };
  for (const [k, v] of Object.entries(pkg.rateLimits ?? {})) {
    if (k in limits) (limits as Record<string, number>)[k] = v;
  }
  const caps = { ...EMPTY_CAPS };
  for (const [k, v] of Object.entries(pkg.features ?? {})) {
    if (k in caps) (caps as Record<string, boolean>)[k] = Boolean(v);
  }
  const limitStrs = {} as Record<keyof PlanLimits, string>;
  for (const f of LIMIT_FIELDS) limitStrs[f.key] = String(limits[f.key] ?? 0);
  return {
    id: pkg.id,
    name: pkg.name,
    tier: "starter",
    priceMonthlyUsd: String(pkg.price ?? 0),
    seatLimit: limits.seats,
    datasetLimit: limits.datasets,
    limits: limitStrs,
    capabilities: caps,
    features: [],
    featuresText: "",
    description: pkg.description ?? "",
    recommended: false,
    waiveFees: Boolean(pkg.waived),
    waiveFeesReason: ""
  };
}

function draftToPlan(d: Draft): { plan?: PlanCatalogEntry; error?: string } {
  if (!d.id.trim()) return { error: "Plan id is required." };
  if (!/^[a-z0-9][a-z0-9-]*[a-z0-9]$/.test(d.id.trim())) {
    return { error: "Plan id must be lowercase letters, digits, and hyphens." };
  }
  if (!d.name.trim()) return { error: "Plan name is required." };
  const price = Number(d.priceMonthlyUsd);
  if (!Number.isFinite(price) || price < 0) return { error: "Price must be a non-negative number." };
  const limits = {} as PlanLimits;
  for (const f of LIMIT_FIELDS) {
    const n = Number(d.limits[f.key]);
    if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n)) {
      return { error: `${f.label} must be a non-negative integer.` };
    }
    limits[f.key] = n;
  }
  const features = d.featuresText.split("\n").map((s) => s.trim()).filter(Boolean);
  return {
    plan: {
      id: d.id.trim(),
      name: d.name.trim(),
      tier: d.tier,
      priceMonthlyUsd: price,
      seatLimit: limits.seats,
      datasetLimit: limits.datasets,
      limits,
      capabilities: d.capabilities,
      features,
      description: (d.description ?? "").trim() || undefined,
      recommended: d.recommended,
      waiveFees: d.waiveFees,
      waiveFeesReason: d.waiveFees ? ((d.waiveFeesReason ?? "").trim() || undefined) : undefined
    }
  };
}

export default function AdminPlans() {
  const [plans, setPlans] = useState<PlanCatalogEntry[]>([]);
  const [packages, setPackages] = useState<SubscriptionPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [editor, setEditor] = useState<Draft | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([api.adminListPlans(), api.subscriptionPackages()])
      .then(([planRes, pkgRes]) => {
        setPlans(planRes.plans ?? []);
        setPackages(pkgRes.packages ?? []);
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const planIds = useMemo(() => new Set(plans.map((p) => p.id)), [plans]);
  const orphanPackages = useMemo(
    () => packages.filter((p) => !planIds.has(p.id)),
    [packages, planIds]
  );

  const tPlans = useTableState(plans as unknown as Array<Record<string, unknown>>, {
    searchFields: ["id", "name", "tier", "description"],
    initialPageSize: 25
  });
  const tOrphans = useTableState(orphanPackages as unknown as Array<Record<string, unknown>>, {
    searchFields: ["id", "name", "description"],
    initialPageSize: 25
  });

  const onCreate = () => { setEditingId(null); setEditor(planToDraft(null)); };
  const onEdit = (plan: PlanCatalogEntry) => { setEditingId(plan.id); setEditor(planToDraft(plan)); };
  const onConvertPackage = (pkg: SubscriptionPackage) => {
    setEditingId(null);
    setEditor(packageToPlanDraft(pkg));
  };
  const onCancel = () => setEditor(null);

  const onDelete = async (plan: PlanCatalogEntry) => {
    if (!confirm(`Delete plan "${plan.name}"? This will fail if any subscription is using it.`)) return;
    try {
      await api.adminDeletePlan(plan.id);
      setBanner(`Deleted plan "${plan.id}".`);
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  const onDeleteOrphanPackage = async (pkg: SubscriptionPackage) => {
    if (!confirm(`Delete legacy package "${pkg.id}"? Workspaces on this package will fall back to the free plan.`)) return;
    try {
      await api.deleteSubscriptionPackage(pkg.id);
      setBanner(`Deleted legacy package "${pkg.id}".`);
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!editor) return;
    const { plan, error: validationError } = draftToPlan(editor);
    if (!plan) { setError(validationError ?? "Invalid plan."); return; }
    setSaving(true); setError(null);
    try {
      if (editingId) {
        await api.adminUpdatePlan(editingId, plan);
        setBanner(`Updated plan "${plan.id}".`);
      } else {
        await api.adminCreatePlan(plan);
        setBanner(`Created plan "${plan.id}".`);
      }
      setEditor(null);
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Subscription plans</h1>
          <p>
            Catalog plans shown on the public landing page and assigned to workspaces. Toggle <strong>Waive fees</strong> to
            grant a plan at $0. This is the single source of truth — every plan write mirrors into the legacy package
            store automatically.
          </p>
        </div>
        <button className="btn btn-primary" type="button" onClick={onCreate}>New plan</button>
      </header>

      {error && <div className="alert alert-error">{error}</div>}
      {banner && <div className="alert alert-ok">{banner}</div>}

      {loading ? <SkeletonTable rows={5} columns={4} /> :
       plans.length === 0 ? (
        <EmptyState icon="🧾" title="No plans defined" description="Create at least one plan to populate the public pricing page." />
       ) : (
        <div>
          <TableToolbar
            search={tPlans.search} setSearch={tPlans.setSearch}
            page={tPlans.page} setPage={tPlans.setPage}
            pageSize={tPlans.pageSize} setPageSize={tPlans.setPageSize}
            totalPages={tPlans.totalPages} totalRows={tPlans.totalRows}
            hideExport
            searchPlaceholder="Search plan id / name / tier…"
          />
          <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Tier</th>
                <th>Effective price</th>
                <th>Capabilities</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(tPlans.paged as unknown as PlanCatalogEntry[]).map((p) => {
                const effective = p.effectivePriceMonthlyUsd ?? (p.waiveFees ? 0 : p.priceMonthlyUsd);
                const capCount = Object.values(p.capabilities).filter(Boolean).length;
                return (
                  <tr key={p.id}>
                    <td><code>{p.id}</code></td>
                    <td>
                      <strong>{p.name}</strong>
                      {p.recommended ? <span className="pill pill-info" style={{ marginLeft: 6 }}>Recommended</span> : null}
                      {p.description ? <div className="soft" style={{ fontSize: 12 }}>{p.description}</div> : null}
                    </td>
                    <td className="soft">{p.tier}</td>
                    <td>
                      {p.waiveFees ? (
                        <span><span className="pill pill-ok">Fees waived</span> <s className="muted">${p.priceMonthlyUsd.toLocaleString()}</s></span>
                      ) : effective === 0 ? (
                        <span className="pill pill-ok">Free</span>
                      ) : (
                        <span>${effective.toLocaleString()}/mo</span>
                      )}
                    </td>
                    <td className="soft">{capCount} / {CAPABILITY_FIELDS.length}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <button className="btn btn-sm" type="button" onClick={() => onEdit(p)}>Edit</button>{" "}
                      <button className="btn btn-sm btn-danger" type="button" onClick={() => onDelete(p)}>Delete</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
       )}

      {!loading && orphanPackages.length > 0 && (
        <section className="section" style={{ marginTop: 24 }}>
          <div className="section-head">
            <h2>Legacy packages without a plan</h2>
            <span className="soft">{orphanPackages.length} orphan{orphanPackages.length === 1 ? "" : "s"}</span>
          </div>
          <p className="soft" style={{ fontSize: 13 }}>
            These entries exist in the legacy <code>SubscriptionPackage</code> store but have no matching plan.
            Convert them to a plan to bring them under canonical management, or delete them if unused.
          </p>
          <TableToolbar
            search={tOrphans.search} setSearch={tOrphans.setSearch}
            page={tOrphans.page} setPage={tOrphans.setPage}
            pageSize={tOrphans.pageSize} setPageSize={tOrphans.setPageSize}
            totalPages={tOrphans.totalPages} totalRows={tOrphans.totalRows}
            hideExport
            searchPlaceholder="Search package id / name…"
          />
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Price</th>
                  <th>Features</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(tOrphans.paged as unknown as SubscriptionPackage[]).map((p) => (
                  <tr key={p.id}>
                    <td><code>{p.id}</code></td>
                    <td><strong>{p.name}</strong></td>
                    <td>{p.waived || p.price === 0 ? <span className="pill pill-ok">Free</span> : <span>${p.price.toFixed(2)}</span>}</td>
                    <td className="soft" style={{ maxWidth: 320 }}>
                      {Object.entries(p.features).filter(([, v]) => v).map(([k]) => k).join(", ") || <span className="muted">none</span>}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <button className="btn btn-sm" type="button" onClick={() => onConvertPackage(p)}>Convert to plan</button>{" "}
                      <button className="btn btn-sm btn-danger" type="button" onClick={() => onDeleteOrphanPackage(p)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <PlanEditor
        draft={editor}
        existing={editingId != null}
        saving={saving}
        onChange={setEditor}
        onCancel={onCancel}
        onSubmit={onSubmit}
      />
    </div>
  );
}

function PlanEditor({
  draft, existing, saving, onChange, onCancel, onSubmit
}: {
  draft: Draft | null;
  existing: boolean;
  saving: boolean;
  onChange: (d: Draft) => void;
  onCancel: () => void;
  onSubmit: (e: FormEvent) => void;
}) {
  if (!draft) {
    return (
      <Drawer open={false} title="" onClose={onCancel}>{null}</Drawer>
    );
  }
  const setCap = (key: keyof PlanCapabilities, value: boolean) =>
    onChange({ ...draft, capabilities: { ...draft.capabilities, [key]: value } });
  const setLimit = (key: keyof PlanLimits, value: string) =>
    onChange({ ...draft, limits: { ...draft.limits, [key]: value } });

  return (
    <Drawer
      open={true}
      title={existing ? `Edit plan · ${draft.name || draft.id}` : "New plan"}
      subtitle={existing ? <code>{draft.id}</code> : "Pricing, limits, and capability flags"}
      onClose={onCancel}
      width={760}
      asForm
      onSubmit={onSubmit}
      footer={(
        <>
          <button type="button" className="btn" onClick={onCancel}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? "Saving…" : existing ? "Save changes" : "Create plan"}
          </button>
        </>
      )}
    >
      <div className="field-row">
        <div className="field">
          <label>Plan ID</label>
          <input type="text" value={draft.id} onChange={(e) => onChange({ ...draft, id: e.target.value })} disabled={existing} placeholder="e.g. professional" required />
          <small className="muted">Lowercase, hyphens only. Cannot be changed after creation.</small>
        </div>
        <div className="field">
          <label>Name</label>
          <input type="text" value={draft.name} onChange={(e) => onChange({ ...draft, name: e.target.value })} required />
        </div>
        <div className="field">
          <label>Tier</label>
          <select value={draft.tier} onChange={(e) => onChange({ ...draft, tier: e.target.value as Draft["tier"] })}>
            {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>

      <div className="field-row">
        <div className="field">
          <label>Price (USD / month)</label>
          <input type="number" min={0} step={0.01} value={draft.priceMonthlyUsd} onChange={(e) => onChange({ ...draft, priceMonthlyUsd: e.target.value })} disabled={draft.waiveFees} required />
          {draft.waiveFees && <small className="muted">Effective price is $0 while fees are waived.</small>}
        </div>
        <div className="field" style={{ alignSelf: "end" }}>
          <label>
            <input type="checkbox" checked={draft.recommended} onChange={(e) => onChange({ ...draft, recommended: e.target.checked })} />
            {" "}Recommended (highlight on landing page)
          </label>
        </div>
        <div className="field" style={{ alignSelf: "end" }}>
          <label>
            <input type="checkbox" checked={draft.waiveFees} onChange={(e) => onChange({ ...draft, waiveFees: e.target.checked })} />
            {" "}<strong>Waive fees</strong> — grant at $0
          </label>
        </div>
      </div>

      {draft.waiveFees && (
        <div className="field">
          <label>Reason for waiving fees (optional, audit-visible)</label>
          <input type="text" maxLength={280} value={draft.waiveFeesReason} onChange={(e) => onChange({ ...draft, waiveFeesReason: e.target.value })} placeholder="e.g. Government partnership, pilot program" />
        </div>
      )}

      <div className="field">
        <label>Description</label>
        <textarea rows={2} value={draft.description ?? ""} onChange={(e) => onChange({ ...draft, description: e.target.value })} />
      </div>

      <fieldset className="field" style={{ border: "1px solid var(--border)", borderRadius: 8, padding: 12 }}>
        <legend><strong>Capabilities</strong> (toggle each module)</legend>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 8 }}>
          {CAPABILITY_FIELDS.map((f) => (
            <label key={f.key} style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <input
                type="checkbox"
                checked={Boolean(draft.capabilities[f.key])}
                onChange={(e) => setCap(f.key, e.target.checked)}
              />
              <span>{f.label}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset className="field" style={{ border: "1px solid var(--border)", borderRadius: 8, padding: 12 }}>
        <legend><strong>Limits</strong></legend>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10 }}>
          {LIMIT_FIELDS.map((f) => (
            <label key={f.key} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <span>{f.label}{f.help ? <span className="muted" style={{ fontWeight: 400 }}> · {f.help}</span> : null}</span>
              <input
                type="number"
                min={0}
                step={1}
                value={draft.limits[f.key]}
                onChange={(e) => setLimit(f.key, e.target.value)}
              />
            </label>
          ))}
        </div>
      </fieldset>

      <div className="field">
        <label>Features (one per line, shown as bullet points on landing page)</label>
        <textarea
          rows={5}
          value={draft.featuresText}
          onChange={(e) => onChange({ ...draft, featuresText: e.target.value })}
          placeholder="Up to 5 DHIS2 instances&#10;Priority support&#10;SSO included"
        />
      </div>
    </Drawer>
  );
}
